import React, { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import {
  AgentDto,
  useCampaignControllerCampaignQuery,
} from '@shared/services/apiService/apiService';
import { AgentList } from './components/AgentList';
import Spinner from '@shared/components/spinner/Spinner';
import { Box } from '@mui/material';
import AgentSection from './components/AgentSection';

interface AgentPageProps {
  isLoading: boolean;
  refetch: () => Promise<void>;
  onAgentCreated?: () => Promise<void>;
  agents: AgentDto[];
  isProductDetailsDirty: boolean;
  isSavingProductDetails: boolean;
  onProductDetailsSave: () => Promise<boolean>;
  onProductDetailsCancel: () => void;
  productDetailsRevision: number;
  onEditorDirtyChange?: (dirty: boolean) => void;
}

export const AgentPage: React.FC<AgentPageProps> = ({
  agents: agentList,
  refetch,
  onAgentCreated,
  isLoading,
  isProductDetailsDirty,
  isSavingProductDetails,
  onProductDetailsSave,
  onProductDetailsCancel,
  productDetailsRevision,
  onEditorDirtyChange,
}) => {
  const [loadingFlag, setLoadingFlag] = useState(false);
  const { campaignId } = useParams();
  const [searchParams] = useSearchParams();

  const { currentData: campaignData, isFetching: isCampaignLoading } =
    useCampaignControllerCampaignQuery({ id: campaignId || '' }, { skip: !campaignId });

  const squad = campaignData?.squad;
  const isSquad = !!squad;
  const squadName = squad?.name || null;

  const displayAgents = isSquad && squad?.agents ? squad.agents : agentList;

  const setLoading = async (flag: boolean) => {
    await refetch();
    setLoadingFlag(flag);
  };

  if (isLoading || loadingFlag || (isCampaignLoading && !campaignData)) {
    return <Spinner />;
  }

  return (
    <>
      <AgentList
        key={campaignData?.id ?? campaignId}
        onRefresh={refetch}
        onAgentCreated={onAgentCreated}
        setLoading={setLoading}
        agentList={displayAgents || []}
        isSquad={isSquad}
        campaignData={campaignData}
        requestedAgentId={searchParams.get('agentId')}
        requestedCreate={searchParams.get('createAgent') === 'guided'}
        onEditorDirtyChange={onEditorDirtyChange}
      />

      <Box mt={4}>
        <AgentSection
          isSquad={isSquad}
          squadName={squadName}
          campaignId={campaignId}
          onSquadDeleted={refetch}
          isProductDetailsDirty={isProductDetailsDirty}
          isSavingProductDetails={isSavingProductDetails}
          onProductDetailsSave={onProductDetailsSave}
          onProductDetailsCancel={onProductDetailsCancel}
          productDetailsRevision={productDetailsRevision}
        />
      </Box>
    </>
  );
};
